"use client";

import styles from "./NavigationBar.module.css";
import classNames from "classnames/bind";
import { NLXLogo } from "../icons/NLXLogo";
import Link from "next/link";
import { Button } from "../Button/Button";
import { useState } from "react";
import { usePathname } from "next/navigation";
import { useEffect } from "react";

const cx = classNames.bind(styles);

export default function NavigationBar({
  navigationItems,
  ctaButtonLabel,
  ctaButtonUrl,
  menuButtonLabel,
  closeMenuButtonLabel,
  theme = "default",
}: {
  navigationItems: {
    id?: string | null;
    label: string;
    route: string;
    hidden?: boolean | null;
  }[];
  ctaButtonLabel: string;
  ctaButtonUrl: string;
  menuButtonLabel: string;
  closeMenuButtonLabel: string;
  theme?: "default" | "light";
}) {
  const [mobileMenuIsOpen, setMobileMenuIsOpen] = useState(false);
  const pathname = usePathname();

  // Special nav logic for home and platform (and all /platform subpages)
  const isSpecialNav = pathname === "/" || pathname.startsWith("/platform");
  const [hideNav, setHideNav] = useState(false);

  const handleMenuButtonClick = () => {
    setMobileMenuIsOpen((current) => !current);
  };

  useEffect(() => {
    if (!isSpecialNav) return;

    const heroId =
      pathname === "/"
        ? "homepage-hero"
        : pathname.startsWith("/platform")
          ? "platform-hero"
          : null;

    if (!heroId) return;
    const hero = document.getElementById(heroId);
    if (!hero) return;

    const handleScroll = () => {
      const heroRect = hero.getBoundingClientRect();
      setHideNav(heroRect.bottom <= 0);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, [pathname, isSpecialNav]);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 768px)");
    const handleResize = () => {
      if (mediaQuery.matches) setMobileMenuIsOpen(false);
    };
    mediaQuery.addEventListener("change", handleResize);
    return () => {
      mediaQuery.removeEventListener("change", handleResize);
    };
  }, []);

  return (
    <header
      className={cx(
        "container",
        // isSpecialNav && "NavigationBar--home",
        // isSpecialNav && hideNav && "NavigationBar--hidden",
        // theme === "light" && "NavigationBar--light",
      )}
      data-variant={isSpecialNav ? "fixed" : undefined}
      data-hidden={isSpecialNav && hideNav ? "true" : undefined}
      data-theme={theme === "light" ? "light" : undefined}
    >
      <nav
        className={cx("inner")}
        aria-labelledby="site-nav-label"
        id="site-nav"
        data-nav-opened={mobileMenuIsOpen ? "true" : undefined}
      >
        <span id="site-nav-label" hidden>
          Main site navigation
        </span>

        <Link href="/" className={cx("logo")} aria-label="Home">
          <NLXLogo />
        </Link>

        <button
          id="nav-toggle"
          className={cx("menu-button")}
          aria-controls="site-nav-list"
          aria-expanded={mobileMenuIsOpen}
          onClick={handleMenuButtonClick}
        >
          {mobileMenuIsOpen ? closeMenuButtonLabel : menuButtonLabel}
        </button>

        {mobileMenuIsOpen && (
          <div
            className={cx("nav-overlay")}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            data-open={mobileMenuIsOpen ? "true" : undefined}
          >
            <div className={cx("nav-overlay-content")}>
              {navigationItems
                .filter((item) => !item.hidden)
                .map((item) => {
                  const isCurrent = pathname === item.route;
                  return (
                    <div
                      className={cx("navigation-item")}
                      key={item.id}
                      onClick={() => setMobileMenuIsOpen(false)}
                    >
                      <Link
                        href={item.route}
                        aria-current={isCurrent ? "page" : undefined}
                      >
                        {item.label}
                      </Link>
                    </div>
                  );
                })}
              <Button
                className={cx("cta-button", "cta-button-mobile")}
                onClick={() => setMobileMenuIsOpen(false)}
              >
                {ctaButtonLabel}
              </Button>
            </div>
          </div>
        )}

        <ul id="site-nav-list" role="list" className={cx("items-container")}>
          {navigationItems
            .filter((item) => !item.hidden)
            .map((item) => {
              const isCurrent = pathname === item.route;
              return (
                <li className={cx("navigation-item")} key={item.id}>
                  <Link
                    href={item.route}
                    aria-current={isCurrent ? "page" : undefined}
                  >
                    {item.label}
                  </Link>
                </li>
              );
            })}
        </ul>
        <Button
          className={cx("cta-button", "cta-button-desktop")}
          href={ctaButtonUrl}
        >
          {ctaButtonLabel}
        </Button>
        {/* TODO: add Canvas button */}
      </nav>
    </header>
  );
}
